import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import { Button } from '@/common/ui/button'
import { Input } from '@/common/ui/input'
import { Label } from '@/common/ui/label'
import { Textarea } from '@/common/ui/textarea'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { Skeleton } from '@/common/ui/skeleton'
import { Calendar } from '@/common/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/common/ui/popover'
import { Checkbox } from '@/common/ui/checkbox'
import { CalendarIcon, Search } from 'lucide-react'
import { format } from 'date-fns'
import { th } from 'date-fns/locale'
import { cn } from '@/lib/utils'
import useQueryGetMessageConditions from '../../api/hooks/useQueryGetMessageConditions'
import useQueryGetAutoMessageConditions from '../../api/hooks/useQueryGetAutoMessageConditions'
import useMutationGroupPlayersByCondition from '../../api/hooks/useMutationGroupPlayersByCondition'
import { getPlayerTags } from '@/modules/tags/api/services'
import { TagsQueryKeys } from '@/modules/tags/constants'
import {
	MessageConditionType,
	MessageCondition,
	MessageConditionSingle,
	MessageConditionAuto,
	GetPlayerByConditionRequest,
} from '../../types'
import {
	getConditionLabel,
	getDateRangeSectionLabel,
	getDateRangeHelpText,
	getAmountFieldLabels,
	getSingleValueLabel,
} from '../../utils/conditionLabels'

interface MessageConditionFilterProps {
	isAutoMode?: boolean
	onPlayersQueried: (
		phoneNumbers: string[],
		lineIds: string[],
		condition: GetPlayerByConditionRequest,
	) => void
}

export const MessageConditionFilter = ({
	isAutoMode = false,
	onPlayersQueried,
}: MessageConditionFilterProps) => {
	const { data: singleConditionsData, isLoading: isLoadingSingleConditions } =
		useQueryGetMessageConditions({ enabled: !isAutoMode })
	const { data: autoConditionsData, isLoading: isLoadingAutoConditions } =
		useQueryGetAutoMessageConditions({ enabled: isAutoMode })

	const conditionsData = isAutoMode ? autoConditionsData : singleConditionsData
	const isLoadingConditions = isAutoMode
		? isLoadingAutoConditions
		: isLoadingSingleConditions

	const groupPlayerMutation = useMutationGroupPlayersByCondition()

	const [activeCondition, setActiveCondition] = useState<
		MessageConditionSingle | MessageConditionAuto | ''
	>('')
	const [conditionValues, setConditionValues] = useState<{
		minimum?: number
		maximum?: number
	}>({})
	const [phoneNumbersInput, setPhoneNumbersInput] = useState('')
	const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])
	const [dateRange, setDateRange] = useState<{
		startDate?: Date
		endDate?: Date
	}>({})
	// Key to force Select component to re-render on reset
	const [selectKey, setSelectKey] = useState(0)

	// Fetch player tags for CUSTOMER_TAGS condition
	const { data: playerTags, isLoading: isLoadingTags } = useQuery({
		queryKey: [TagsQueryKeys.GetTags],
		queryFn: () => getPlayerTags(),
		enabled:
			activeCondition === MessageConditionSingle.CUSTOMER_TAGS && !isAutoMode,
		staleTime: 5 * 60 * 1000,
	})

	const handleConditionChange = (conditionName: string) => {
		setActiveCondition(
			conditionName as MessageConditionSingle | MessageConditionAuto,
		)
		setConditionValues({})
		setPhoneNumbersInput('')
		setSelectedTagIds([])
		setDateRange({})
	}

	const handleValueChange = (field: 'minimum' | 'maximum', value: string) => {
		const numValue = value === '' ? undefined : Number(value)
		setConditionValues((prev) => ({
			...prev,
			[field]: numValue,
		}))
	}

	/**
	 * Validates and normalizes a Thai phone number
	 * Accepts formats: 66XXXXXXXXX, 0XXXXXXXXX, 66-X-XXXX-XXXX, 0X-XXXX-XXXX
	 * Returns normalized format or null if invalid
	 */
	const validateThaiPhoneNumber = (phone: string): string | null => {
		// Remove all hyphens and spaces
		const cleaned = phone.replace(/[-\s]/g, '')

		// Check if starts with 66 followed by 9 digits
		if (/^66\d{9}$/.test(cleaned)) {
			return cleaned
		}

		// Check if starts with 0 followed by 9 digits
		if (/^0\d{9}$/.test(cleaned)) {
			return cleaned
		}

		return null
	}

	const currentCondition = conditionsData?.conditions?.find(
		(c) => c.name === activeCondition,
	)

	const getValidationError = () => {
		const { minimum, maximum } = conditionValues
		if (minimum !== undefined && maximum !== undefined && minimum > maximum) {
			return 'ค่าต่ำสุดต้องน้อยกว่าหรือเท่ากับค่าสูงสุด'
		}

		// Validate phone numbers if condition type is PHONE_NUMBERS
		if (
			currentCondition?.type === MessageConditionType.PHONE_NUMBERS &&
			phoneNumbersInput.trim()
		) {
			const phoneNumbers = phoneNumbersInput
				.split(',')
				.map((p) => p.trim())
				.filter(Boolean)

			if (phoneNumbers.length === 0) {
				return 'กรุณากรอกเบอร์โทรศัพท์อย่างน้อย 1 หมายเลข'
			}

			// Validate each phone number
			for (const phone of phoneNumbers) {
				if (!validateThaiPhoneNumber(phone)) {
					return `เบอร์โทรศัพท์ "${phone}" ไม่ถูกต้อง (ต้องขึ้นต้นด้วย 66 หรือ 0 และมีตัวเลข 10 หลัก)`
				}
			}
		}

		// Validate tags if condition is CUSTOMER_TAGS
		if (
			activeCondition === MessageConditionSingle.CUSTOMER_TAGS &&
			selectedTagIds.length === 0
		) {
			return 'กรุณาเลือกแท็กอย่างน้อย 1 แท็ก'
		}

		return null
	}

	const hasValidationError = getValidationError() !== null

	const handleSearch = async () => {
		if (!activeCondition) {
			return
		}

		if (!currentCondition) {
			return
		}

		try {
			// Cast to MessageCondition for API request (backend uses combined enum)
			// Both MessageConditionSingle and MessageConditionAuto are subsets of MessageCondition
			const requestData: GetPlayerByConditionRequest = {
				condition: activeCondition as unknown as MessageCondition,
				...conditionValues,
			}

			// Handle phone numbers input for PHONE_NUMBERS condition type
			if (currentCondition.type === MessageConditionType.PHONE_NUMBERS) {
				const phoneNumbers = phoneNumbersInput
					.split(',')
					.map((p) => p.trim())
					.filter(Boolean)
					.map((phone) => validateThaiPhoneNumber(phone))
					.filter((phone): phone is string => phone !== null)

				requestData.phoneNumbers = phoneNumbers
			}

			// Handle tag IDs for CUSTOMER_TAGS condition
			if (activeCondition === MessageConditionSingle.CUSTOMER_TAGS) {
				requestData.tagIds = selectedTagIds
			}

			// Include date range if condition supports it
			if (currentCondition.supportsDateRange) {
				if (dateRange.startDate) {
					requestData.startDate = dateRange.startDate.toISOString()
				}
				if (dateRange.endDate) {
					requestData.endDate = dateRange.endDate.toISOString()
				}
			}

			const players = await groupPlayerMutation.mutateAsync(requestData)
			if (players) {
				// Extract phone numbers and LINE IDs from players (filter out null/undefined)
				const phoneNumbers = players
					.map((p) => p.phone)
					.filter((phone): phone is string => Boolean(phone))

				const lineIds = players
					.map((p) => p.lineId)
					.filter((lineId): lineId is string => Boolean(lineId))

				onPlayersQueried(phoneNumbers, lineIds, requestData)
			}
		} catch (error) {
			console.error('Error in search:', error)
		}
	}

	const handleReset = () => {
		setActiveCondition('')
		setConditionValues({})
		setPhoneNumbersInput('')
		setSelectedTagIds([])
		setDateRange({})
		// Increment key to force Select component to re-render and show placeholder
		setSelectKey((prev) => prev + 1)
		// Reset mutation state to clear results display
		groupPlayerMutation.reset()
		// Reset without querying - parent should handle empty state
		onPlayersQueried([], [], {
			condition: MessageCondition.ALL_MEMBERS,
		})
	}

	if (isLoadingConditions) {
		return (
			<Card>
				<CardHeader>
					<CardTitle>เงื่อนไขการค้นหา</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<Skeleton className="h-10 w-full" />
					<Skeleton className="h-10 w-full" />
					<Skeleton className="h-10 w-full" />
				</CardContent>
			</Card>
		)
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle>เงื่อนไขการค้นหา</CardTitle>
			</CardHeader>
			<CardContent className="space-y-6">
				<div className="space-y-4">
					<Label>เลือกเงื่อนไข</Label>
					<Select
						key={selectKey}
						value={activeCondition || undefined}
						onValueChange={handleConditionChange}
					>
						<SelectTrigger>
							<SelectValue placeholder="เลือกเงื่อนไขในการค้นหา" />
						</SelectTrigger>
						<SelectContent>
							{conditionsData?.conditions?.map((condition) => (
								<SelectItem key={condition.name} value={condition.name}>
									{getConditionLabel(condition.name)}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				{currentCondition && (
					<div className="space-y-4 border-t pt-4">
						<div className="space-y-2">
							<Label>{getConditionLabel(currentCondition.name)}</Label>
						</div>

						{currentCondition.type === MessageConditionType.SINGLE && (
							<div className="space-y-2">
								<Label htmlFor={`${currentCondition.name}-value`}>
									{getSingleValueLabel(currentCondition.name)}
								</Label>
								<Input
									id={`${currentCondition.name}-value`}
									type="number"
									placeholder="กรอกค่าที่ต้องการ"
									value={conditionValues.minimum ?? ''}
									onChange={(e) => handleValueChange('minimum', e.target.value)}
								/>
							</div>
						)}

						{currentCondition.type === MessageConditionType.MIN_MAX && (
							<div className="space-y-2">
								<div className="grid grid-cols-2 gap-4">
									<div className="space-y-2">
										<Label htmlFor={`${currentCondition.name}-min`}>
											{getAmountFieldLabels(currentCondition.name).min}
										</Label>
										<Input
											id={`${currentCondition.name}-min`}
											type="number"
											placeholder="ค่าต่ำสุด"
											value={conditionValues.minimum ?? ''}
											onChange={(e) =>
												handleValueChange('minimum', e.target.value)
											}
										/>
									</div>
									<div className="space-y-2">
										<Label htmlFor={`${currentCondition.name}-max`}>
											{getAmountFieldLabels(currentCondition.name).max}
										</Label>
										<Input
											id={`${currentCondition.name}-max`}
											type="number"
											placeholder="ค่าสูงสุด"
											value={conditionValues.maximum ?? ''}
											onChange={(e) =>
												handleValueChange('maximum', e.target.value)
											}
										/>
									</div>
								</div>
							</div>
						)}

						{currentCondition.type === MessageConditionType.PHONE_NUMBERS && (
							<div className="space-y-2">
								<Label htmlFor={`${currentCondition.name}-phones`}>
									เบอร์โทรศัพท์
								</Label>
								<Textarea
									id={`${currentCondition.name}-phones`}
									placeholder="กรอกเบอร์โทรศัพท์ คั่นด้วยเครื่องหมายคอมม่า (,)&#10;ตัวอย่าง: 0812345678, 0823456789, 66812345678"
									value={phoneNumbersInput}
									onChange={(e) => setPhoneNumbersInput(e.target.value)}
									rows={5}
									className="font-mono text-sm"
								/>
								<p className="text-xs text-muted-foreground">
									รองรับรูปแบบ: 0XXXXXXXXX หรือ 66XXXXXXXXX (สามารถมีขีดกลาง -
									ได้)
								</p>
							</div>
						)}

						{activeCondition === MessageConditionSingle.CUSTOMER_TAGS && (
							<div className="space-y-2">
								<Label>เลือกแท็ก</Label>
								{isLoadingTags ? (
									<div className="space-y-2">
										<Skeleton className="h-8 w-full" />
										<Skeleton className="h-8 w-full" />
										<Skeleton className="h-8 w-full" />
									</div>
								) : playerTags && playerTags.length > 0 ? (
									<div className="max-h-60 space-y-2 overflow-y-auto rounded-md border p-3">
										{playerTags.map((tag) => (
											<div key={tag.id} className="flex items-center space-x-2">
												<Checkbox
													id={`tag-${tag.id}`}
													checked={selectedTagIds.includes(tag.id)}
													onCheckedChange={(checked) => {
														if (checked) {
															setSelectedTagIds((prev) => [...prev, tag.id])
														} else {
															setSelectedTagIds((prev) =>
																prev.filter((id) => id !== tag.id),
															)
														}
													}}
												/>
												<Label
													htmlFor={`tag-${tag.id}`}
													className="flex cursor-pointer items-center gap-2 font-normal"
												>
													<span
														className="rounded px-2 py-0.5 text-xs"
														style={{
															backgroundColor: tag.color || '#e5e7eb',
															color: '#000',
														}}
													>
														{tag.name}
													</span>
													<span className="text-xs text-muted-foreground">
														({tag.playerCount} คน)
													</span>
												</Label>
											</div>
										))}
									</div>
								) : (
									<p className="text-sm text-muted-foreground">
										ไม่มีแท็กในระบบ กรุณาสร้างแท็กก่อน
									</p>
								)}
								<p className="text-xs text-muted-foreground">
									เลือกแท็กที่ต้องการค้นหาสมาชิก (สามารถเลือกได้หลายแท็ก)
								</p>
							</div>
						)}

						{currentCondition.supportsDateRange && (
							<div className="space-y-2">
								<Label>
									{getDateRangeSectionLabel(currentCondition.name)} (ไม่บังคับ)
								</Label>
								<div className="grid grid-cols-2 gap-4">
									<div className="space-y-2">
										<Label
											htmlFor={`${currentCondition.name}-start-date`}
											className="text-sm text-muted-foreground"
										>
											วันที่เริ่มต้น
										</Label>
										<Popover>
											<PopoverTrigger asChild>
												<Button
													id={`${currentCondition.name}-start-date`}
													variant="outline"
													className={cn(
														'w-full justify-start text-left font-normal',
														!dateRange.startDate && 'text-muted-foreground',
													)}
												>
													<CalendarIcon className="mr-2 h-4 w-4" />
													{dateRange.startDate
														? format(dateRange.startDate, 'PPP', { locale: th })
														: 'เลือกวันที่'}
												</Button>
											</PopoverTrigger>
											<PopoverContent className="w-auto p-0" align="start">
												<Calendar
													mode="single"
													selected={dateRange.startDate}
													onSelect={(date) =>
														setDateRange((prev) => ({
															...prev,
															startDate: date,
														}))
													}
													initialFocus
												/>
											</PopoverContent>
										</Popover>
									</div>
									<div className="space-y-2">
										<Label
											htmlFor={`${currentCondition.name}-end-date`}
											className="text-sm text-muted-foreground"
										>
											วันที่สิ้นสุด
										</Label>
										<Popover>
											<PopoverTrigger asChild>
												<Button
													id={`${currentCondition.name}-end-date`}
													variant="outline"
													className={cn(
														'w-full justify-start text-left font-normal',
														!dateRange.endDate && 'text-muted-foreground',
													)}
												>
													<CalendarIcon className="mr-2 h-4 w-4" />
													{dateRange.endDate
														? format(dateRange.endDate, 'PPP', { locale: th })
														: 'เลือกวันที่'}
												</Button>
											</PopoverTrigger>
											<PopoverContent className="w-auto p-0" align="start">
												<Calendar
													mode="single"
													selected={dateRange.endDate}
													onSelect={(date) =>
														setDateRange((prev) => ({
															...prev,
															endDate: date,
														}))
													}
													initialFocus
												/>
											</PopoverContent>
										</Popover>
									</div>
								</div>
								<p className="text-xs text-muted-foreground">
									{getDateRangeHelpText(currentCondition.name)}
								</p>
							</div>
						)}

						{hasValidationError && (
							<p className="text-sm text-red-500">{getValidationError()}</p>
						)}
					</div>
				)}

				<div className="flex gap-2">
					<Button
						onClick={handleSearch}
						disabled={
							!activeCondition ||
							groupPlayerMutation.isPending ||
							hasValidationError
						}
						className="flex-1"
					>
						<Search className="mr-2 h-4 w-4" />
						{groupPlayerMutation.isPending ? 'กำลังค้นหา...' : 'ค้นหา'}
					</Button>
					<Button
						variant="outline"
						onClick={handleReset}
						disabled={groupPlayerMutation.isPending}
					>
						ล้าง
					</Button>
				</div>

				{groupPlayerMutation.data && (
					<div className="border-t pt-4">
						<p className="text-sm text-muted-foreground">
							พบสมาชิกที่ตรงตามเงื่อนไข{' '}
							<span className="font-semibold">
								{groupPlayerMutation.data.length}
							</span>{' '}
							คน
						</p>
					</div>
				)}
			</CardContent>
		</Card>
	)
}
